/** Geographic math used by the map and AR modules. */

export interface LatLng {
  lat: number;
  lon: number;
}

const EARTH_RADIUS_M = 6371000;
const M_PER_NM = 1852;
const M_PER_FT = 0.3048;

const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

/** Great-circle distance in metres. */
export function haversineM(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(h));
}

export function haversineNm(a: LatLng, b: LatLng): number {
  return haversineM(a, b) / M_PER_NM;
}

/** Initial great-circle bearing from a to b, degrees clockwise from north (0..360). */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const dLon = toRad(b.lon - a.lon);
  const y = Math.sin(dLon) * Math.cos(la2);
  const x =
    Math.cos(la1) * Math.sin(la2) - Math.sin(la1) * Math.cos(la2) * Math.cos(dLon);
  return (toDeg(Math.atan2(y, x)) + 360) % 360;
}

/**
 * Elevation angle (degrees) from observer to aircraft, above the horizon.
 * `userAltM` and `acAltM` are altitudes in metres (acAltM = baro altitude in ft -> m).
 */
export function elevationDeg(
  a: LatLng,
  userAltM: number,
  b: LatLng,
  acAltM: number,
): number {
  const groundDist = haversineM(a, b);
  const heightDiff = acAltM - userAltM;
  if (groundDist <= 1) return heightDiff >= 0 ? 90 : -90;
  return toDeg(Math.atan2(heightDiff, groundDist));
}

/** Smallest signed difference between two angles in degrees (-180..180). */
export function angleDelta(a: number, b: number): number {
  let d = ((a - b + 540) % 360) - 180;
  return d;
}

/** Advance a position by `distM` metres along `bearingDeg`. */
export function project(from: LatLng, bearingDeg: number, distM: number): LatLng {
  const d = distM / EARTH_RADIUS_M;
  const br = toRad(bearingDeg);
  const la1 = toRad(from.lat);
  const lo1 = toRad(from.lon);
  const la2 = Math.asin(
    Math.sin(la1) * Math.cos(d) + Math.cos(la1) * Math.sin(d) * Math.cos(br),
  );
  const lo2 =
    lo1 +
    Math.atan2(
      Math.sin(br) * Math.sin(d) * Math.cos(la1),
      Math.cos(d) - Math.sin(la1) * Math.sin(la2),
    );
  return { lat: toDeg(la2), lon: ((toDeg(lo2) + 540) % 360) - 180 };
}

/** Max dead-reckoning horizon for the general map fleet. */
export const MAX_EXTRAP_SEC = 15;
/** Tighter cap while an aircraft is selected — avoids coasting far ahead of stale tiles. */
export const MAX_EXTRAP_SELECTED_SEC = 4;

/**
 * Seconds since the last known position for one aircraft, combining per-airframe
 * `seen` (age at snapshot time) with wall-clock age of the snapshot itself.
 */
export function positionAgeSec(ac: { seen: number }, snapshotTimeSec: number, nowSec: number): number {
  return Math.max(0, ac.seen + (nowSec - snapshotTimeSec));
}

/** Dead-reckoning interval capped by fleet vs selected limits. */
export function extrapolationDtSec(
  ac: { seen: number; icao: string },
  snapshotTimeSec: number,
  nowSec: number,
  selectedIcao: string | null,
): number {
  const cap = ac.icao === selectedIcao ? MAX_EXTRAP_SELECTED_SEC : MAX_EXTRAP_SEC;
  return Math.min(cap, positionAgeSec(ac, snapshotTimeSec, nowSec));
}

/** Per-airframe dt including a dedicated fast-refresh sample for the selection. */
export function extrapolationDtFor(
  ac: { seen: number; icao: string },
  snapshotTimeSec: number,
  nowSec: number,
  selectedIcao: string | null,
  selectedFetchedAtSec: number,
): number {
  if (selectedFetchedAtSec > 0 && ac.icao === selectedIcao) {
    return Math.min(2, Math.max(0, nowSec - selectedFetchedAtSec));
  }
  return extrapolationDtSec(ac, snapshotTimeSec, nowSec, selectedIcao);
}

/** Advance an aircraft by `dtSec` seconds using its ground speed + track. */
export function extrapolate(
  ac: { lat: number | null; lon: number | null; groundSpeed: number | null; track: number | null },
  dtSec: number,
): { lat: number; lon: number } | null {
  if (ac.lat == null || ac.lon == null || ac.groundSpeed == null || ac.track == null) return null;
  const distM = (ac.groundSpeed * 0.514444) * dtSec; // knots -> m/s
  if (distM <= 0.5) return { lat: ac.lat, lon: ac.lon };
  return project({ lat: ac.lat, lon: ac.lon }, ac.track, distM);
}

export const FT_TO_M = M_PER_FT;
export function ftToM(ft: number): number {
  return ft * M_PER_FT;
}
